import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { usePagBank } from '../hooks/usePagBank';
import { buildApiUrl } from '../services/api';
import { useGlobalSettings } from '../hooks/useGlobalSettings';
import { AppliedCoupon, createEmptyPaymentFormData, PaymentFormData } from '../types/payment';
import {
  getCouponDiscountPercent,
  getCouponFreeDays,
  getCouponSummaryText,
  isFreeDaysCoupon,
  normalizeCouponCode,
  parseCouponValidationResponse
} from '../utils/couponUtils';
import { DEFAULT_BILLING_OFFER_CODE, formatCurrency, getBillingOffer } from '../utils/billingOffers';
import { maskCPF } from '../utils/documentUtils';
import { applyBrandName, getBrandName, getDefaultLogoPath } from '../utils/branding';
import { normalizePhoneInput, validatePhone } from '../utils/phoneUtils';

const isValidCPF = (value: string): boolean => {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;

  const numbers = digits.split('').map(Number);

  const firstSum = numbers.slice(0, 9).reduce((sum, num, index) => sum + num * (10 - index), 0);
  let firstCheck = (firstSum * 10) % 11;
  if (firstCheck === 10) firstCheck = 0;
  if (firstCheck !== numbers[9]) return false;

  const secondSum = numbers.slice(0, 10).reduce((sum, num, index) => sum + num * (11 - index), 0);
  let secondCheck = (secondSum * 10) % 11;
  if (secondCheck === 10) secondCheck = 0;

  return secondCheck === numbers[10];
};

const registerSchema = z.object({
  nome: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  email: z.string().email('E-mail inválido'),
  telefone: z.string().refine(validatePhone, 'Telefone inválido'),
  document: z.string().min(11, 'CPF é obrigatório').refine(isValidCPF, 'CPF inválido'),
  registroNumero: z.string().min(2, 'Número de registro é obrigatório'),
  orgaoEmissor: z.string().min(2, 'Órgão emissor é obrigatório'),
  documentoComprovante: z
    .custom<FileList>()
    .refine((files) => files instanceof FileList && files.length > 0, 'Documento de comprovação é obrigatório'),
  senha: z.string()
    .min(6, 'Senha deve ter pelo menos 6 caracteres')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Senha deve conter minúscula, maiúscula e número'),
  confirmSenha: z.string().min(6, 'Confirmação de senha é obrigatória')
}).refine((data) => data.senha === data.confirmSenha, {
  path: ['confirmSenha'],
  message: 'As senhas não conferem'
});

type RegisterFormData = z.infer<typeof registerSchema>;

const registerFeatures = [
  'Certidões automáticas com IA',
  'Banco de certidões com IA',
  'Envio de mensagens em massa',
  'Reenvio de mensagens estratégico',
  'Banco de mensagens salvas',
  'Gestão por categorias de destinatários'
];

const registerMetrics = [
  { value: '+5k', label: 'mandados processados' },
  { value: '98%', label: 'precisão da IA' },
  { value: '24/7', label: 'disponível' }
];

const whatsappSupportUrl = 'https://wa.me/5561993584522?text=Estou%20tendo%20problemas%20com%20cadastro%20do%20AuxiliaJud%20e%20preciso%20de%20ajuda.';

const termsText = `TERMO DE USO E CONCORDÂNCIA DO APLICATIVO AUXILIAJUD

Pelo presente instrumento particular, de um lado VIA BOOTS SOLUÇÕES EM TECNOLOGIA LTDA, pessoa jurídica de direito privado, CNPJ 61.438.361/0001-09, doravante denominada LICENCIANTE, desenvolvedora e mantenedora do aplicativo AUXILIAJUD, e, de outro lado, o USUÁRIO, devidamente identificado no momento do cadastro, ajustam entre si o presente TERMO DE USO, que se regerá pelas cláusulas e condições a seguir.

CLÁUSULA 1 - DO OBJETO

1.1. O presente Termo tem por objeto regular as condições de uso não exclusivo, pessoal e intransferível do aplicativo AUXILIAJUD, destinado exclusivamente a oficiais de justiça, com a finalidade de auxiliar na organização, no registro e no apoio ao cumprimento de mandados judiciais, por meio de ferramentas digitais.

1.2. O AUXILIAJUD constitui ferramenta de apoio, não substituindo sistemas oficiais do Poder Judiciário, nem alterando, suprimindo ou ampliando as atribuições legais do usuário.

CLÁUSULA 2 - DA NATUREZA DO USO

2.1. O presente instrumento trata-se exclusivamente de contrato de uso, não estabelecendo qualquer vínculo empregatício, funcional, associativo, societário ou institucional entre o usuário, o AUXILIAJUD, a Via Boots Soluções em Tecnologia LTDA ou o Poder Judiciário.

2.2. O uso do aplicativo é realizado por conta e risco exclusivo do usuário, que permanece integralmente responsável por seus atos funcionais, registros, comunicações e pelo cumprimento das normas legais, regulamentares e administrativas aplicáveis à sua atividade.

CLÁUSULA 3 - DAS RESPONSABILIDADES DO USUÁRIO

3.1. O usuário declara, sob sua exclusiva responsabilidade, que:

I - É oficial de justiça regularmente investido no cargo;
II - Utilizará o aplicativo de forma ética, lícita e compatível com suas atribuições funcionais;
III - É integralmente responsável pela veracidade das informações inseridas no sistema.

3.2. O usuário compromete-se a:

I - Não utilizar o aplicativo para fins ilícitos, indevidos ou contrários às normas do órgão ao qual esteja vinculado;
II - Manter o sigilo sobre informações sensíveis, dados pessoais e conteúdos protegidos por segredo de justiça;
III - Zelar pela segurança de suas credenciais de acesso (login e senha), respondendo por qualquer uso indevido decorrente de sua negligência.

CLÁUSULA 4 - DAS LIMITAÇÕES DE RESPONSABILIDADE

4.1. O AUXILIAJUD e a Via Boots Soluções em Tecnologia LTDA não se responsabilizam, em nenhuma hipótese:

I - Por decisões administrativas, funcionais ou disciplinares eventualmente aplicadas ao usuário;
II - Pelo uso inadequado, indevido ou em desconformidade com este Termo;
III - Por falhas decorrentes de conexão à internet, dispositivos do usuário ou serviços de terceiros.

4.2. O AUXILIAJUD não se responsabiliza por regras, políticas, termos ou normas estipuladas pela empresa Meta Platforms, Inc. (Meta), incluindo, mas não se limitando, a políticas anti-spam, restrições de envio de mensagens, bloqueios, suspensões ou limitações impostas por seus serviços, tais como WhatsApp, Instagram ou Facebook.

4.3. O usuário declara estar ciente de que a Meta possui normas próprias e independentes, e que qualquer penalidade aplicada por aquela empresa não gera responsabilidade solidária ou subsidiária da Via Boots Soluções em Tecnologia LTDA.

CLÁUSULA 5 - DA VIGÊNCIA E RENOVAÇÃO

5.1. O presente Termo é celebrado por prazo determinado, conforme o plano ou modalidade de assinatura contratada pelo usuário.

5.2. O contrato será renovado automaticamente por igual período ao término de sua vigência, salvo manifestação contrária do usuário, a ser realizada com antecedência mínima de 10 (dez) dias antes do vencimento.

5.3. A manifestação contrária deverá ocorrer por meio das funcionalidades disponíveis no aplicativo ou por canal oficial indicado pela Via Boots Soluções em Tecnologia LTDA.

CLÁUSULA 6 - DA RESCISÃO, CANCELAMENTO E RESTITUIÇÃO

6.1. O presente Termo poderá ser rescindido:

I - A qualquer tempo, por iniciativa do usuário;
II - Pela Via Boots Soluções, em caso de violação das disposições deste Termo;
III - Por descontinuidade do aplicativo, mediante aviso prévio razoável.

6.2. A rescisão não gera direito a indenização de qualquer natureza, ressalvadas as hipóteses expressamente previstas neste Termo.

6.3. No caso de cancelamento da assinatura mensal recorrente, será respeitado o prazo legal de arrependimento, nos termos da legislação vigente, hipótese em que será assegurada a restituição integral dos valores pagos, observadas as regras do meio de pagamento utilizado.

6.4. Decorrido o prazo legal de cancelamento, o pedido de rescisão poderá ser realizado a qualquer tempo; contudo, não haverá restituição, total ou parcial, dos valores pagos, permanecendo o acesso ao serviço ativo até o término do período contratado, salvo disposição expressa em contrário.

6.5. O usuário declara estar ciente de que a política de cancelamento ora estabelecida é compatível com a natureza do serviço prestado e com a disponibilização contínua da plataforma durante o período contratado.

CLÁUSULA 7 - DA PROTEÇÃO DE DADOS

7.1. O tratamento de dados pessoais observará integralmente a Lei Geral de Proteção de Dados - LGPD (Lei nº 13.709/2018).

7.2. Os dados coletados serão utilizados exclusivamente para:

I - Funcionamento do aplicativo;
II - Melhoria contínua dos serviços;
III - Cumprimento de obrigações legais e regulatórias.

CLÁUSULA 8 - DA PROPRIEDADE INTELECTUAL

8.1. O aplicativo AUXILIAJUD, suas funcionalidades, layout, marca, código-fonte e demais elementos são de propriedade exclusiva da Via Boots Soluções em Tecnologia LTDA, sendo vedada qualquer reprodução, modificação, engenharia reversa ou uso não autorizado.

CLÁUSULA 9 - DAS DISPOSIÇÕES GERAIS

9.1. A eventual tolerância de qualquer das partes quanto ao descumprimento de cláusulas não implicará novação ou renúncia de direitos.

9.2. Caso qualquer disposição deste Termo seja considerada inválida ou inexequível, as demais permanecerão em pleno vigor.

CLÁUSULA 10 - DO FORO

10.1. Para dirimir quaisquer controvérsias oriundas deste Termo, fica eleito o foro da comarca de domicílio da Via Boots Soluções em Tecnologia LTDA, com renúncia expressa a qualquer outro, por mais privilegiado que seja.

ACEITE

Ao clicar em "LI E CONCORDO COM OS TERMOS DE USO", o usuário declara que leu, compreendeu e concorda integralmente com todas as cláusulas deste Termo.`;

function CheckIcon() {
  return (
    <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-300" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="m5 12 4 4 10-10" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg className="h-5 w-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2a10 10 0 0 0-8.78 14.79L2 22l5.34-1.18A10 10 0 1 0 12 2Zm0 18a8 8 0 0 1-4.07-1.11l-.29-.17-3.17.7.71-3.09-.19-.31A8 8 0 1 1 12 20Zm4.39-5.46c-.24-.12-1.43-.71-1.65-.79-.22-.08-.38-.12-.54.12-.16.24-.62.79-.76.95-.14.16-.28.18-.52.06-.24-.12-1-.37-1.91-1.18-.71-.63-1.19-1.41-1.33-1.65-.14-.24-.01-.37.11-.49.11-.11.24-.28.36-.42.12-.14.16-.24.24-.4.08-.16.04-.3-.02-.42-.06-.12-.54-1.3-.74-1.79-.2-.47-.41-.41-.56-.41h-.48c-.16 0-.42.06-.64.3-.22.24-.84.82-.84 2s.86 2.32.98 2.48c.12.16 1.69 2.58 4.1 3.62.57.25 1.02.4 1.37.52.58.18 1.11.16 1.53.1.47-.07 1.43-.58 1.63-1.14.2-.56.2-1.04.14-1.14-.06-.1-.22-.16-.46-.28Z" />
    </svg>
  );
}

export function RegisterPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [pendingData, setPendingData] = useState<RegisterFormData | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [isEncryptingCard, setIsEncryptingCard] = useState(false);
  const [selectedBillingOfferCode, setSelectedBillingOfferCode] = useState(DEFAULT_BILLING_OFFER_CODE);
  const [paymentData, setPaymentData] = useState<PaymentFormData>(createEmptyPaymentFormData);
  const [paymentErrors, setPaymentErrors] = useState<Record<string, string>>({});
  const [isCepLoading, setIsCepLoading] = useState(false);
  const [lastCepLookup, setLastCepLookup] = useState('');
  const [registrationAttemptId, setRegistrationAttemptId] = useState<string | null>(null);

  const navigate = useNavigate();
  const { settings } = useGlobalSettings();
  const { encryptCard } = usePagBank();
  const brandName = getBrandName();
  const defaultLogoPath = getDefaultLogoPath();
  const pageTitle = applyBrandName(settings?.pageTitle) || brandName;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      nome: '',
      email: '',
      telefone: '',
      document: '',
      registroNumero: '',
      orgaoEmissor: '',
      senha: '',
      confirmSenha: ''
    }
  });

  const selectedBillingOffer = getBillingOffer(selectedBillingOfferCode);
  const appliedDiscountPercent = getCouponDiscountPercent(appliedCoupon);
  const isTrialCoupon = isFreeDaysCoupon(appliedCoupon);
  const trialDays = getCouponFreeDays(appliedCoupon);
  const requiresImmediatePayment = !isTrialCoupon;
  const discountMultiplier = 1 - appliedDiscountPercent / 100;
  const discountedAmountCents = Math.max(0, Math.round(selectedBillingOffer.amountCents * discountMultiplier));
  const installmentAmount = formatCurrency(discountedAmountCents);
  const originalAmount = formatCurrency(selectedBillingOffer.amountCents);
  const annualRecurringDisplay = `12 x R$ ${installmentAmount}`;

  useEffect(() => {
    if (!showTermsModal && !showPaymentModal) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [showTermsModal, showPaymentModal]);

  useEffect(() => {
    const cepDigits = paymentData.billingZip.replace(/\D/g, '');
    if (cepDigits.length !== 8 || cepDigits === lastCepLookup) return;

    let isActive = true;
    const timer = setTimeout(async () => {
      setIsCepLoading(true);
      try {
        const response = await fetch(buildApiUrl(`/cep/${cepDigits}`));
        const data = await response.json().catch(() => null);

        if (!response.ok) {
          if (isActive) {
            setPaymentErrors((prev) => ({ ...prev, billingZip: data?.error || 'CEP não encontrado' }));
            setLastCepLookup(cepDigits);
          }
          return;
        }

        if (!isActive || !data) return;

        setPaymentData((prev) => ({
          ...prev,
          billingStreet: prev.billingStreet || data.street || '',
          billingDistrict: prev.billingDistrict || data.district || '',
          billingCity: prev.billingCity || data.city || '',
          billingState: prev.billingState || data.state || '',
          billingComplement: prev.billingComplement || data.complement || ''
        }));

        setPaymentErrors((prev) => {
          const next = { ...prev };
          delete next.billingZip;
          delete next.billingStreet;
          delete next.billingDistrict;
          delete next.billingCity;
          delete next.billingState;
          return next;
        });

        setLastCepLookup(cepDigits);
      } catch {
        if (isActive) {
          setPaymentErrors((prev) => ({ ...prev, billingZip: 'Erro ao consultar CEP' }));
          setLastCepLookup(cepDigits);
        }
      } finally {
        if (isActive) setIsCepLoading(false);
      }
    }, 350);

    return () => {
      isActive = false;
      clearTimeout(timer);
    };
  }, [paymentData.billingZip, lastCepLookup]);

  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 19);
    return digits.replace(/(.{4})/g, '$1 ').trim();
  };

  const formatExpiry = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  };

  const formatCep = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 5) return digits;
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  };

  const validatePayment = () => {
    if (isTrialCoupon) {
      setPaymentErrors({});
      return true;
    }

    const nextErrors: Record<string, string> = {};
    const cardDigits = paymentData.cardNumber.replace(/\D/g, '');
    const cepDigits = paymentData.billingZip.replace(/\D/g, '');

    if (!paymentData.holderName.trim()) nextErrors.holderName = 'Nome do titular é obrigatório';
    if (!isValidCPF(paymentData.holderDocument)) nextErrors.holderDocument = 'CPF do titular inválido';
    if (cardDigits.length < 13) nextErrors.cardNumber = 'Número do cartão inválido';
    if (!/^\d{2}\/\d{2}$/.test(paymentData.cardExpiry)) nextErrors.cardExpiry = 'Validade inválida';
    if (!/^\d{3,4}$/.test(paymentData.cardCvv)) nextErrors.cardCvv = 'CVV inválido';
    if (cepDigits.length < 8) nextErrors.billingZip = 'CEP inválido';
    if (!paymentData.billingStreet.trim()) nextErrors.billingStreet = 'Endereço é obrigatório';
    if (!paymentData.billingNumber.trim()) nextErrors.billingNumber = 'Número é obrigatório';
    if (!paymentData.billingDistrict.trim()) nextErrors.billingDistrict = 'Bairro é obrigatório';
    if (!paymentData.billingCity.trim()) nextErrors.billingCity = 'Cidade é obrigatória';
    if (!/^[A-Za-z]{2}$/.test(paymentData.billingState.trim())) nextErrors.billingState = 'UF inválida';

    setPaymentErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const submitRegistration = async (data: RegisterFormData, encryptedCard?: string) => {
    setIsSubmitting(true);
    let wasSuccessful = false;

    try {
      const documentFile = data.documentoComprovante?.item(0);
      if (!documentFile) throw new Error('Documento de comprovação é obrigatório');

      const formData = new FormData();
      formData.append('nome', data.nome);
      formData.append('email', data.email);
      formData.append('telefone', data.telefone);
      formData.append('document', data.document);
      formData.append('senha', data.senha);
      formData.append('registroNumero', data.registroNumero);
      formData.append('orgaoEmissor', data.orgaoEmissor);
      formData.append('termsAccepted', 'true');
      formData.append('billingOfferCode', selectedBillingOffer.code);
      formData.append('documentoComprovante', documentFile);
      if (registrationAttemptId) formData.append('registrationAttemptId', registrationAttemptId);

      if (encryptedCard) {
        formData.append('contactPhone', paymentData.contactPhone || data.telefone);
        formData.append('holderName', paymentData.holderName);
        formData.append('holderDocument', paymentData.holderDocument);
        formData.append('billingZip', paymentData.billingZip);
        formData.append('billingStreet', paymentData.billingStreet);
        formData.append('billingNumber', paymentData.billingNumber);
        formData.append('billingComplement', paymentData.billingComplement);
        formData.append('billingDistrict', paymentData.billingDistrict);
        formData.append('billingCity', paymentData.billingCity);
        formData.append('billingState', paymentData.billingState);
        formData.append('cardCvv', paymentData.cardCvv);
        formData.append('cardEncrypted', encryptedCard);
      }

      if (appliedCoupon?.code) formData.append('couponCode', appliedCoupon.code);

      const response = await fetch(buildApiUrl('/auth/register-public'), {
        method: 'POST',
        body: formData
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Erro ao criar conta');

      toast.success('Conta criada com sucesso! Em breve você receberá a confirmação por e-mail.');
      navigate('/login');
      wasSuccessful = true;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao criar conta');
    } finally {
      setIsSubmitting(false);
    }

    return wasSuccessful;
  };

  const createRegistrationAttempt = async (data: RegisterFormData) => {
    const response = await fetch(buildApiUrl('/auth/register-attempt'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        registrationAttemptId,
        nome: data.nome,
        email: data.email,
        telefone: data.telefone,
        document: data.document,
        billingOfferCode: selectedBillingOffer.code
      })
    });

    if (!response.ok) {
      const result = await response.json().catch(() => null);
      throw new Error(result?.message || 'Não foi possível iniciar o cadastro');
    }

    const result = await response.json().catch(() => null);
    const nextAttemptId = result?.data?.registrationAttemptId;
    if (nextAttemptId) setRegistrationAttemptId(nextAttemptId);
  };

  const handleApplyCoupon = async () => {
    const normalized = normalizeCouponCode(couponCode);
    if (!normalized) {
      setCouponError('Informe um cupom válido');
      return;
    }

    setIsApplyingCoupon(true);
    setCouponError('');

    try {
      const response = await fetch(buildApiUrl('/coupons/validate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: normalized })
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) throw new Error(data?.message || data?.error || 'Cupom inválido');

      const parsedCoupon = parseCouponValidationResponse(data, normalized);
      setAppliedCoupon(parsedCoupon);
      setCouponCode(normalized);
      setCouponError('');

      if (isFreeDaysCoupon(parsedCoupon)) setPaymentErrors({});
    } catch (error) {
      setCouponError(error instanceof Error ? error.message : 'Erro ao validar cupom');
      setAppliedCoupon(null);
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  const onSubmit = async (data: RegisterFormData) => {
    setPaymentData((prev) => ({
      ...prev,
      contactPhone: data.telefone
    }));

    try {
      await createRegistrationAttempt(data);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Não foi possível iniciar o cadastro');
    }

    setPendingData(data);
    setTermsAccepted(false);
    setShowTermsModal(true);
  };

  const confirmTermsAndSubmit = async () => {
    if (!pendingData) return;
    setShowTermsModal(false);
    setShowPaymentModal(true);
  };

  const handlePaymentSubmit = async () => {
    if (!pendingData) return;

    if (!isTrialCoupon && !validatePayment()) return;

    let encryptedCard: string | undefined;
    if (!isTrialCoupon) {
      try {
        setIsEncryptingCard(true);
        encryptedCard = await encryptCard(paymentData);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Erro ao criptografar cartão');
        return;
      } finally {
        setIsEncryptingCard(false);
      }
    }

    const wasSuccessful = await submitRegistration(pendingData, encryptedCard);
    if (!wasSuccessful) return;

    setShowPaymentModal(false);
    setPendingData(null);
    setRegistrationAttemptId(null);
    setPaymentData(createEmptyPaymentFormData());
    setPaymentErrors({});
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  return (
    <>
      <div className="min-h-screen bg-slate-950 px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-7xl items-center justify-center">
          <div className="w-full overflow-hidden rounded-2xl border border-white/10 bg-white/5 shadow-[0_24px_70px_rgba(2,6,23,0.5)] backdrop-blur-xl">
            <div className="grid lg:grid-cols-[1.08fr_0.92fr]">
              <section
                className="relative overflow-hidden p-6 text-white sm:p-9 lg:p-10"
                style={{ background: 'linear-gradient(145deg, #071326 0%, #0C2747 48%, #0D1B2F 100%)' }}
              >
                <div className="absolute -left-16 -top-16 h-52 w-52 rounded-full bg-sky-300/10 blur-3xl" />
                <div className="absolute -bottom-20 right-6 h-64 w-64 rounded-full bg-blue-500/10 blur-3xl" />

                <div className="relative space-y-8">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/15 bg-white/10">
                      {settings?.iconUrl ? (
                        <img src={settings.iconUrl} alt={brandName} className="h-7 w-7 object-contain" style={{ filter: 'brightness(0) invert(1)' }} />
                      ) : (
                        <img src={defaultLogoPath} alt={brandName} className="h-7 w-7 object-contain" style={{ filter: 'brightness(0) invert(1)' }} />
                      )}
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-sky-100/85">{pageTitle}</p>
                      <p className="text-sm text-sky-100/75">Acelerador de Cumprimento de Mandados</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h1 className="text-3xl font-semibold leading-tight text-white sm:text-4xl lg:text-5xl">Crie sua conta institucional</h1>
                    <p className="max-w-2xl text-sm leading-7 text-slate-100/90 sm:text-base">
                      Cadastre-se para automatizar certidões, comunicação e gestão de mandados em um só ambiente.
                    </p>
                  </div>

                  <ul className="grid gap-3 sm:grid-cols-2">
                    {registerFeatures.map((feature) => (
                      <li key={feature} className="flex items-start gap-2.5 rounded-xl border border-white/10 bg-white/5 px-3.5 py-3 text-sm text-slate-100">
                        <CheckIcon />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="grid gap-3 sm:grid-cols-3">
                    {registerMetrics.map((metric) => (
                      <div key={metric.label} className="rounded-xl border border-white/10 bg-slate-950/25 px-4 py-3.5">
                        <p className="text-2xl font-semibold text-white">{metric.value}</p>
                        <p className="mt-1 text-xs text-slate-300">{metric.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              <section className="bg-white p-6 sm:p-9 lg:p-10">
                <div className="mx-auto max-w-md">
                  <div className="space-y-2">
                    <h2 className="text-3xl font-semibold tracking-tight text-slate-900">Cadastro</h2>
                    <p className="text-sm text-slate-500 sm:text-base">Preencha seus dados para criar a conta</p>
                  </div>

                  <div className="mb-6 mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                    <strong>Aviso:</strong> o sistema é voltado exclusivamente para oficiais de justiça.
                  </div>

                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                    <div>
                      <label htmlFor="nome" className="mb-2 block text-sm font-medium text-gray-700">Nome completo</label>
                      <input {...register('nome')} id="nome" type="text" className="w-full rounded-xl border border-gray-300 px-4 py-3" placeholder="Seu nome completo" disabled={isSubmitting} />
                      {errors.nome && <p className="mt-2 text-sm text-red-600">{errors.nome.message}</p>}
                    </div>

                    <div>
                      <label htmlFor="email" className="mb-2 block text-sm font-medium text-gray-700">E-mail</label>
                      <input {...register('email')} id="email" type="email" className="w-full rounded-xl border border-gray-300 px-4 py-3" placeholder="seu@email.com" disabled={isSubmitting} />
                      {errors.email && <p className="mt-2 text-sm text-red-600">{errors.email.message}</p>}
                    </div>

                    <div>
                      <label htmlFor="telefone" className="mb-2 block text-sm font-medium text-gray-700">Telefone</label>
                      <input
                        {...register('telefone', { onChange: (e) => setValue('telefone', normalizePhoneInput(e.target.value)) })}
                        id="telefone"
                        type="text"
                        className="w-full rounded-xl border border-gray-300 px-4 py-3"
                        placeholder="(61) 99999-9999"
                        disabled={isSubmitting}
                      />
                      {errors.telefone && <p className="mt-2 text-sm text-red-600">{errors.telefone.message}</p>}
                    </div>

                    <div>
                      <label htmlFor="document" className="mb-2 block text-sm font-medium text-gray-700">CPF</label>
                      <input
                        {...register('document', { onChange: (e) => setValue('document', maskCPF(e.target.value)) })}
                        id="document"
                        type="text"
                        className="w-full rounded-xl border border-gray-300 px-4 py-3"
                        placeholder="Apenas números"
                        disabled={isSubmitting}
                      />
                      {errors.document && <p className="mt-2 text-sm text-red-600">{errors.document.message}</p>}
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="registroNumero" className="mb-2 block text-sm font-medium text-gray-700">Número de registro</label>
                        <input {...register('registroNumero')} id="registroNumero" type="text" className="w-full rounded-xl border border-gray-300 px-4 py-3" placeholder="Ex: 12345" disabled={isSubmitting} />
                        {errors.registroNumero && <p className="mt-2 text-sm text-red-600">{errors.registroNumero.message}</p>}
                      </div>
                      <div>
                        <label htmlFor="orgaoEmissor" className="mb-2 block text-sm font-medium text-gray-700">Órgão emissor</label>
                        <input {...register('orgaoEmissor')} id="orgaoEmissor" type="text" className="w-full rounded-xl border border-gray-300 px-4 py-3" placeholder="Ex: TJ" disabled={isSubmitting} />
                        {errors.orgaoEmissor && <p className="mt-2 text-sm text-red-600">{errors.orgaoEmissor.message}</p>}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="documentoComprovante" className="mb-2 block text-sm font-medium text-gray-700">Documento de comprovação (PDF ou imagem)</label>
                      <input {...register('documentoComprovante')} id="documentoComprovante" type="file" accept="application/pdf,image/*" className="w-full rounded-xl border border-gray-300 px-4 py-3" disabled={isSubmitting} />
                      <p className="mt-2 text-xs text-gray-500">Envie o comprovante de oficial de justiça para validação da conta.</p>
                      {errors.documentoComprovante && <p className="mt-2 text-sm text-red-600">{errors.documentoComprovante.message}</p>}
                    </div>

                    <div>
                      <label htmlFor="senha" className="mb-2 block text-sm font-medium text-gray-700">Senha</label>
                      <div className="relative">
                        <input {...register('senha')} id="senha" type={showPassword ? 'text' : 'password'} className="w-full rounded-xl border border-gray-300 px-4 py-3 pr-12" placeholder="Mín. 6 caracteres com maiúscula, minúscula e número" disabled={isSubmitting} />
                        <button type="button" onClick={() => setShowPassword((prev) => !prev)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500" disabled={isSubmitting}>
                          {showPassword ? 'Ocultar' : 'Mostrar'}
                        </button>
                      </div>
                      {errors.senha && <p className="mt-2 text-sm text-red-600">{errors.senha.message}</p>}
                    </div>

                    <div>
                      <label htmlFor="confirmSenha" className="mb-2 block text-sm font-medium text-gray-700">Confirmar senha</label>
                      <div className="relative">
                        <input {...register('confirmSenha')} id="confirmSenha" type={showConfirmPassword ? 'text' : 'password'} className="w-full rounded-xl border border-gray-300 px-4 py-3 pr-12" placeholder="Repita a senha" disabled={isSubmitting} />
                        <button type="button" onClick={() => setShowConfirmPassword((prev) => !prev)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500" disabled={isSubmitting}>
                          {showConfirmPassword ? 'Ocultar' : 'Mostrar'}
                        </button>
                      </div>
                      {errors.confirmSenha && <p className="mt-2 text-sm text-red-600">{errors.confirmSenha.message}</p>}
                    </div>

                    <div className="space-y-3 rounded-xl border border-gray-200 bg-slate-50 p-4">
                      <label className="block text-sm font-medium text-gray-700">Plano de assinatura</label>
                      <select value={selectedBillingOfferCode} onChange={(e) => setSelectedBillingOfferCode(e.target.value as typeof selectedBillingOfferCode)} className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm" disabled={isSubmitting}>
                        <option value="MONTHLY_RECURRING">Mensal recorrente | R$ 109,90/mês | renovação automática</option>
                        <option value="ANNUAL_RECURRING">Anual recorrente | 12x de R$ 89,90 | sem cobrança total no cartão</option>
                        <option value="ANNUAL_UPFRONT">Anual à vista | R$ 863,04 em cobrança única</option>
                      </select>
                      <p className="text-xs text-slate-500">{selectedBillingOffer.description}</p>
                    </div>

                    <button type="submit" disabled={isSubmitting} className="w-full rounded-xl bg-slate-900 px-6 py-3 font-medium text-white disabled:cursor-not-allowed disabled:opacity-50">
                      {isSubmitting ? 'Criando conta...' : 'Criar conta'}
                    </button>
                    <a
                      href={whatsappSupportUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-600 bg-emerald-500 px-6 py-3 text-center font-semibold text-white shadow-sm transition hover:bg-emerald-600"
                    >
                      <WhatsAppIcon />
                      Precisa de ajuda no cadastro? Falar no WhatsApp
                    </a>
                  </form>

                  <div className="mt-6 text-center">
                    <p className="text-sm text-slate-600">
                      Já tem conta? <Link to="/login" className="font-semibold text-blue-700 transition hover:text-blue-800">Entrar</Link>
                    </p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      {showTermsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6 sm:p-8">
          <div className="w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 text-sm">
              <h3 className="text-lg font-semibold text-gray-900">Termo de uso</h3>
              <button type="button" onClick={() => setShowTermsModal(false)} className="text-gray-500 transition-colors hover:text-gray-700" disabled={isSubmitting}>Fechar</button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto whitespace-pre-line px-6 py-4 text-sm text-gray-700">{termsText}</div>
            <div className="border-t border-gray-200 px-6 py-4">
              <label className="flex items-start gap-3 text-sm text-gray-800">
                <input type="checkbox" className="mt-1 h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-2" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} disabled={isSubmitting} />
                <span>LI E CONCORDO COM OS TERMOS DE USO</span>
              </label>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button type="button" onClick={() => setShowTermsModal(false)} className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50" disabled={isSubmitting}>Cancelar</button>
                <button type="button" onClick={confirmTermsAndSubmit} disabled={!termsAccepted || isSubmitting} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-all disabled:cursor-not-allowed disabled:opacity-60">
                  {isTrialCoupon ? 'Continuar' : 'Continuar para pagamento'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{requiresImmediatePayment ? 'Pagamento' : 'Ativar dias grátis'}</h3>
                <p className="text-sm text-gray-500">
                  {requiresImmediatePayment ? 'Finalize sua assinatura com cartão de crédito.' : `Seu cupom libera ${trialDays} dias grátis. O pagamento será solicitado depois.`}
                </p>
              </div>
              <button type="button" onClick={() => setShowPaymentModal(false)} className="text-gray-500 transition-colors hover:text-gray-700" disabled={isSubmitting}>Fechar</button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6 text-sm">
              <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
                <div className="space-y-5">
                  <div>
                    <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Dados do titular</h4>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Nome no cartão</label>
                    <input type="text" value={paymentData.holderName} onChange={(e) => setPaymentData((prev) => ({ ...prev, holderName: e.target.value }))} className="w-full rounded-xl border border-gray-300 px-4 py-3" disabled={isSubmitting} />
                    {paymentErrors.holderName && <p className="mt-2 text-sm text-red-600">{paymentErrors.holderName}</p>}
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">CPF do titular</label>
                      <input type="text" value={paymentData.holderDocument} onChange={(e) => setPaymentData((prev) => ({ ...prev, holderDocument: maskCPF(e.target.value) }))} className="w-full rounded-xl border border-gray-300 px-4 py-3" disabled={isSubmitting} />
                      {paymentErrors.holderDocument && <p className="mt-2 text-sm text-red-600">{paymentErrors.holderDocument}</p>}
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">Número do cartão</label>
                      <input type="text" value={paymentData.cardNumber} onChange={(e) => setPaymentData((prev) => ({ ...prev, cardNumber: formatCardNumber(e.target.value) }))} className="w-full rounded-xl border border-gray-300 px-4 py-3" disabled={isSubmitting} />
                      {paymentErrors.cardNumber && <p className="mt-2 text-sm text-red-600">{paymentErrors.cardNumber}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">Validade</label>
                      <input type="text" value={paymentData.cardExpiry} onChange={(e) => setPaymentData((prev) => ({ ...prev, cardExpiry: formatExpiry(e.target.value) }))} placeholder="MM/AA" className="w-full rounded-xl border border-gray-300 px-4 py-3" disabled={isSubmitting} />
                      {paymentErrors.cardExpiry && <p className="mt-2 text-sm text-red-600">{paymentErrors.cardExpiry}</p>}
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">CVV</label>
                      <input type="password" value={paymentData.cardCvv} onChange={(e) => setPaymentData((prev) => ({ ...prev, cardCvv: e.target.value.replace(/\D/g, '').slice(0, 4) }))} className="w-full rounded-xl border border-gray-300 px-4 py-3" disabled={isSubmitting} />
                      {paymentErrors.cardCvv && <p className="mt-2 text-sm text-red-600">{paymentErrors.cardCvv}</p>}
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">Resumo</label>
                      <input
                        type="text"
                        value={selectedBillingOffer.code === 'ANNUAL_RECURRING' ? annualRecurringDisplay : `R$ ${installmentAmount}`}
                        readOnly
                        className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-gray-600"
                      />
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Endereço de cobrança</h4>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">CEP {isCepLoading && <span className="ml-2 text-xs font-medium text-gray-400">Buscando...</span>}</label>
                      <input type="text" value={paymentData.billingZip} onChange={(e) => setPaymentData((prev) => ({ ...prev, billingZip: formatCep(e.target.value) }))} className="w-full rounded-xl border border-gray-300 px-4 py-3" disabled={isSubmitting} />
                      {paymentErrors.billingZip && <p className="mt-2 text-sm text-red-600">{paymentErrors.billingZip}</p>}
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">Endereço</label>
                      <input type="text" value={paymentData.billingStreet} onChange={(e) => setPaymentData((prev) => ({ ...prev, billingStreet: e.target.value }))} className="w-full rounded-xl border border-gray-300 px-4 py-3" disabled={isSubmitting} />
                      {paymentErrors.billingStreet && <p className="mt-2 text-sm text-red-600">{paymentErrors.billingStreet}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">Número</label>
                      <input type="text" value={paymentData.billingNumber} onChange={(e) => setPaymentData((prev) => ({ ...prev, billingNumber: e.target.value }))} className="w-full rounded-xl border border-gray-300 px-4 py-3" disabled={isSubmitting} />
                      {paymentErrors.billingNumber && <p className="mt-2 text-sm text-red-600">{paymentErrors.billingNumber}</p>}
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">Complemento</label>
                      <input type="text" value={paymentData.billingComplement} onChange={(e) => setPaymentData((prev) => ({ ...prev, billingComplement: e.target.value }))} className="w-full rounded-xl border border-gray-300 px-4 py-3" disabled={isSubmitting} />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">Bairro</label>
                      <input type="text" value={paymentData.billingDistrict} onChange={(e) => setPaymentData((prev) => ({ ...prev, billingDistrict: e.target.value }))} className="w-full rounded-xl border border-gray-300 px-4 py-3" disabled={isSubmitting} />
                      {paymentErrors.billingDistrict && <p className="mt-2 text-sm text-red-600">{paymentErrors.billingDistrict}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">Cidade</label>
                      <input type="text" value={paymentData.billingCity} onChange={(e) => setPaymentData((prev) => ({ ...prev, billingCity: e.target.value }))} className="w-full rounded-xl border border-gray-300 px-4 py-3" disabled={isSubmitting} />
                      {paymentErrors.billingCity && <p className="mt-2 text-sm text-red-600">{paymentErrors.billingCity}</p>}
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">UF</label>
                      <input type="text" value={paymentData.billingState} onChange={(e) => setPaymentData((prev) => ({ ...prev, billingState: e.target.value.toUpperCase().slice(0, 2) }))} className="w-full rounded-xl border border-gray-300 px-4 py-3" disabled={isSubmitting} />
                      {paymentErrors.billingState && <p className="mt-2 text-sm text-red-600">{paymentErrors.billingState}</p>}
                    </div>
                  </div>
                </div>

                <div className="space-y-4 rounded-2xl border border-gray-200 bg-gray-50 p-6">
                  <div className="rounded-xl bg-white p-4 shadow-sm">
                    <p className="text-sm text-gray-500">{`${selectedBillingOffer.label} ${brandName}`}</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {selectedBillingOffer.code === 'ANNUAL_RECURRING' ? annualRecurringDisplay : `R$ ${installmentAmount}`}
                    </p>
                    {appliedDiscountPercent > 0 && selectedBillingOffer.code !== 'ANNUAL_RECURRING' && (
                      <p className="text-xs text-gray-400 line-through">R$ {originalAmount}</p>
                    )}
                    <p className="text-sm text-gray-500">{selectedBillingOffer.description}</p>
                  </div>

                  <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
                    <label className="block text-sm font-medium text-gray-700">Cupom de desconto</label>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => {
                          const nextValue = normalizeCouponCode(e.target.value);
                          setCouponCode(nextValue);
                          setCouponError('');
                          if (appliedCoupon && appliedCoupon.code !== nextValue) setAppliedCoupon(null);
                        }}
                        placeholder="Insira o cupom"
                        className="flex-1 rounded-lg border border-gray-300 px-4 py-2"
                        disabled={isSubmitting || isApplyingCoupon}
                      />
                      <button type="button" onClick={appliedCoupon ? handleRemoveCoupon : handleApplyCoupon} disabled={isSubmitting || isApplyingCoupon || (!couponCode.trim() && !appliedCoupon)} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60">
                        {appliedCoupon ? 'Remover' : isApplyingCoupon ? 'Validando...' : 'Aplicar'}
                      </button>
                    </div>
                    {couponError && <p className="text-sm text-red-600">{couponError}</p>}
                    {appliedCoupon && <p className="text-sm text-emerald-700">{getCouponSummaryText(appliedCoupon)}</p>}
                    {isTrialCoupon && <p className="text-xs text-emerald-700">Você terá acesso imediato por {trialDays} dias. Depois disso, será necessário pagar.</p>}
                  </div>

                  {requiresImmediatePayment ? (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                      <strong>Importante:</strong> seu cadastro será ativado após a confirmação do pagamento.
                    </div>
                  ) : (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                      <strong>Dias grátis ativos:</strong> seu acesso será liberado imediatamente por {trialDays} dias.
                    </div>
                  )}

                  <div className="text-xs text-gray-500">
                    {requiresImmediatePayment
                      ? 'Ao continuar, você autoriza a cobrança recorrente mensal no valor informado.'
                      : 'Quando os dias grátis terminarem, você verá uma tela interna para realizar o pagamento.'}
                  </div>
                  <a
                    href={whatsappSupportUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-center gap-2 rounded-xl border border-emerald-600 bg-emerald-500 px-4 py-3 text-center text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-600"
                  >
                    <WhatsAppIcon />
                    Problemas com cadastro? Chamar no WhatsApp
                  </a>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 px-6 py-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button type="button" onClick={() => { setShowPaymentModal(false); setShowTermsModal(true); }} className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50" disabled={isSubmitting}>
                  Voltar
                </button>
                <button type="button" onClick={handlePaymentSubmit} disabled={isSubmitting || isEncryptingCard} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-all disabled:cursor-not-allowed disabled:opacity-60">
                  {isEncryptingCard ? 'Criptografando cartão...' : isSubmitting ? 'Processando...' : isTrialCoupon ? `Concluir cadastro com ${trialDays} dias grátis` : 'Concluir cadastro'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
